/**
 * Language Toggle Component
 * Enhanced language toggle button with accessibility and persistence
 */
import React from 'react';
import { useLanguage } from '../context/LanguageContext';

const LanguageToggle = ({ className = '', size = 'medium', showLabel = false, ...props }) => {
  const { language, toggleLanguage, isLoading } = useLanguage();

  const isRussian = language === 'ru';

  // Keyboard shortcut support
  React.useEffect(() => {
    const handleKeyboardShortcut = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'L') {
        e.preventDefault();
        // Trigger language toggle if not loading
        if (!isLoading) {
          toggleLanguage();
        }
      }
    };

    document.addEventListener('keydown', handleKeyboardShortcut);
    return () => document.removeEventListener('keydown', handleKeyboardShortcut);
  }, [toggleLanguage, isLoading]);

  // Size variants
  const sizeClasses = {
    small: 'text-sm p-1',
    medium: 'text-base p-2',
    large: 'text-lg p-3'
  };

  const buttonClasses = `
    language-toggle
    ${sizeClasses[size]}
    ${className}
    ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
    border-none
    background-none
    transition-all
    duration-200
    hover:scale-110
    focus:outline-none
    focus:ring-2
    focus:ring-blue-500
    focus:ring-offset-2
    rounded
  `.trim().replace(/\s+/g, ' ');

  const handleClick = (e) => {
    e.preventDefault();
    if (!isLoading) {
      toggleLanguage();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e);
    }
  };

  // Flag icons
  const russianFlag = '🇷🇺';
  const englishFlag = '🇬🇧';
  const loadingIcon = '⏳';

  const icon = isLoading ? loadingIcon : (isRussian ? russianFlag : englishFlag);
  const label = isLoading ? 'Switching language...' : `Switch to ${isRussian ? 'English' : 'Russian'}`;

  return (
    <div className="language-toggle-container">
      <button
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={buttonClasses}
        aria-label={label}
        title={label}
        disabled={isLoading}
        type="button"
        {...props}
      >
        <span role="img" aria-hidden="true">
          {icon}
        </span>
        {showLabel && (
          <span className="ml-2 text-sm font-medium">
            {isRussian ? 'RU' : 'EN'}
          </span>
        )}
      </button>
    </div>
  );
};

export default LanguageToggle;